import * as signalR from '@microsoft/signalr';
import { tokenManager } from '../config/apiConfig';
import environmentConfig from '../config/environment';

class SignalRService {
  constructor() {
    this.connection = null;
    this.messageCallbacks = [];
    this.newCallCallbacks = [];
    this.callUpdateCallbacks = [];
    this.driverLocationCallbacks = [];
    this.driverLocationRemovedCallbacks = [];
    this.messageMarkedAsReadCallbacks = [];
    this.rideUpdatedCallbacks = [];
    this.driverApplicationSubmittedCallbacks = [];
  }

  async initialize(hubUrl = environmentConfig.SIGNALR_HUB_URL) {
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
        // CRITICAL: Use a function that fetches a FRESH token each time
        // This ensures reconnections use the current token, not a stale one
        accessTokenFactory: () => tokenManager.getToken() || ''
      })
      .withAutomaticReconnect([0, 1000, 2000, 5000, 10000, 30000, 60000])
      .build();

    // Set up driver location listeners
    this.connection.on('DriverLocationUpdated', (location) => {
      this.driverLocationCallbacks.forEach(cb => cb(location));
    });

    this.connection.on('DriverLocationRemoved', (driverId) => {
      this.driverLocationRemovedCallbacks.forEach(cb => cb(driverId));
    });

    // Handle message marked as read notification
    this.connection.on('MessageMarkedAsRead', (data) => {
      this.messageMarkedAsReadCallbacks.forEach(cb => cb(data));
    });

    // Handle ride updated notification
    this.connection.on('RideUpdated', (data) => {
      this.rideUpdatedCallbacks.forEach(cb => cb(data));
    });

    // Handle driver application submitted
    this.connection.on('DriverApplicationSubmitted', (data) => {
      this.driverApplicationSubmittedCallbacks.forEach(cb => cb(data));
    });

    try {
      await this.connection.start();
    } catch (err) {
      alert('Failed to connect to the server, please contact support.');

      console.error('⚠️  SignalR not available - using HTTP polling fallback');
      console.error('To enable real-time features, implement the SignalR hub on your server');
      this.connection = null; // Clear connection on failure
    }
  }

  async stop() {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      this.messageCallbacks = [];
      this.newCallCallbacks = [];
      this.callUpdateCallbacks = [];
      this.driverLocationCallbacks = [];
      this.driverLocationRemovedCallbacks = [];
      this.rideUpdatedCallbacks = [];
      this.driverApplicationSubmittedCallbacks = [];
    }
  }

  // Message methods

  /**
   * Send a message from dispatcher to a specific driver
   * Uses the DispatcherSendsMessage socket which:
   * 1. Saves the message to database
   * 2. Sends to driver if connected
   * 3. Notifies other dispatchers
   */
  async dispatcherSendsMessage(dispatcherId, driverId, message, rideId = null) {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR not connected');
    }

    try {
      await this.connection.invoke('DispatcherSendsMessage', {
        DispatcherId: dispatcherId,
        DriverId: driverId,
        Message: message,
        RideId: rideId
      });
    } catch (err) {
      console.error('Error sending message to driver:', err);
      throw err;
    }
  }

  // Legacy method - keeping for backward compatibility
  async sendMessageToDriver(driverId, message, rideId = null) {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR not connected');
    }

    try {
      await this.connection.invoke('SendMessageToDriver', {
        driverId,
        message,
        rideId,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error sending message to driver:', err);
      throw err;
    }
  }

  async sendMessageToDispatcher(driverId, message, rideId = null) {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR not connected');
    }

    try {
      await this.connection.invoke('SendMessageToDispatcher', {
        fromDriverId: driverId,
        message,
        rideId,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error sending message to dispatcher:', err);
      throw err;
    }
  }

  onMessageReceived(callback) {
    this.messageCallbacks.push(callback);
    return () => {
      this.messageCallbacks = this.messageCallbacks.filter(cb => cb !== callback);
    };
  }

  // Call notification methods
  async broadcastNewCall(call) {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR not connected');
    }

    try {
      await this.connection.invoke('NewCallCreated', call);
    } catch (err) {
      console.error('Error broadcasting new call:', err);
      throw err;
    }
  }

  async notifyCallUpdate(callId, status, assignedDriverId = null) {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR not connected');
    }

    try {
      await this.connection.invoke('NotifyCallUpdate', {
        callId,
        status,
        assignedDriverId,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error notifying call update:', err);
      throw err;
    }
  }

  onNewCallReceived(callback) {
    this.newCallCallbacks.push(callback);
    return () => {
      this.newCallCallbacks = this.newCallCallbacks.filter(cb => cb !== callback);
    };
  }

  onCallUpdated(callback) {
    this.callUpdateCallbacks.push(callback);
    return () => {
      this.callUpdateCallbacks = this.callUpdateCallbacks.filter(cb => cb !== callback);
    };
  }

  // Driver location tracking methods
  onDriverLocationUpdated(callback) {
    this.driverLocationCallbacks.push(callback);

    return () => {
      this.driverLocationCallbacks = this.driverLocationCallbacks.filter(cb => cb !== callback);
    };
  }

  onDriverLocationRemoved(callback) {
    this.driverLocationRemovedCallbacks.push(callback);

    return () => {
      this.driverLocationRemovedCallbacks = this.driverLocationRemovedCallbacks.filter(cb => cb !== callback);
    };
  }

  async getAllDriverLocations() {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR not connected');
    }

    try {
      return await this.connection.invoke('GetAllDriverLocations');
    } catch (err) {
      console.error('Error getting driver locations:', err);
      throw err;
    }
  }

  // Driver registration (for tracking active drivers)
  async registerDriver(driverId) {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR not connected');
    }

    try {
      await this.connection.invoke('RegisterDriver', driverId);
    } catch (err) {
      console.error('Error registering driver:', err);
      throw err;
    }
  }

  async unregisterDriver(driverId) {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      return; // Don't throw if disconnecting
    }

    try {
      await this.connection.invoke('UnregisterDriver', driverId);
    } catch (err) {
      console.error('Error unregistering driver:', err);
    }
  }

  async registerDispatcher(dispatcherId) {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR not connected');
    }

    try {
      await this.connection.invoke('RegisterDispatcher', dispatcherId);
    } catch (err) {
      console.error('Error registering dispatcher:', err);
      throw err;
    }
  }


  isConnected() {
    return this.connection && this.connection.state === signalR.HubConnectionState.Connected;
  }

  /**
   * Mark messages as read via SignalR
   * This will update the database and notify the sender (driver)
   * 
   * @param {number[]} messageIds - Array of message IDs to mark as read
   */
  async markMessagesAsRead(messageIds) {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.warn('Cannot mark messages as read: SignalR not connected');
      throw new Error('SignalR not connected');
    }

    if (!messageIds || messageIds.length === 0) {
      console.warn('No message IDs provided to mark as read');
      return;
    }

    try {
      await this.connection.invoke('MarkMessagesAsRead', messageIds, 'dispatcher');
    } catch (err) {
      console.error('Failed to mark messages as read:', err);
      throw err;
    }
  }

  /**
   * Subscribe to message marked as read events
   * Called when a driver marks your message as read
   * 
   * @param {function} callback - Callback function to handle read receipt
   * @returns {function} Unsubscribe function
   */
  onMessageMarkedAsRead(callback) {
    this.messageMarkedAsReadCallbacks.push(callback);
    return () => {
      this.messageMarkedAsReadCallbacks = this.messageMarkedAsReadCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Subscribe to ride updated events
   * Called when a ride is updated (by any dispatcher)
   * 
   * @param {function} callback - Callback function with { rideId, ride, changes, timestamp }
   * @returns {function} Unsubscribe function
   */
  onRideUpdated(callback) {
    this.rideUpdatedCallbacks.push(callback);
    return () => {
      this.rideUpdatedCallbacks = this.rideUpdatedCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Subscribe to driver application submitted events (dispatcher only).
   * @param {function} callback - Called with { driverId, email, name, submittedAt }
   * @returns {function} Unsubscribe function
   */
  onDriverApplicationSubmitted(callback) {
    this.driverApplicationSubmittedCallbacks.push(callback);
    return () => {
      this.driverApplicationSubmittedCallbacks = this.driverApplicationSubmittedCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Update a ride via SignalR
   * This will update the ride and notify the assigned driver
   * 
   * @param {object} updateData - The ride update data
   */
  async updateRide(updateData) {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR not connected');
    }

    try {
      await this.connection.invoke('RideUpdated', updateData);
    } catch (err) {
      console.error('Error updating ride:', err);
      throw err;
    }
  }
}

// Export singleton instance
const signalRService = new SignalRService();
export default signalRService;
